/// <amd-module name="Wit/Common/KanbanBoardSettings/Components/BoardSettingsViews/ColumnSettings" />
import { IObservableArray, IObservableValue, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { BoardColumn, BoardColumnType } from "Wit/BoardsHub/Common/Client/Contracts/Settings";
import { ColumnsValidator, IColumnsValidator } from "Wit/BoardsHub/Common/ColumnsValidator";
import { BoardData } from "Wit/BoardsHub/Common/KanbanBoardContracts";
import * as Resources from "Wit/Common/KanbanBoardSettings/Resources";

type IAllowedMappings = { [columnType: string]: { [workItemType: string]: string[] } };

export interface IColumnsSettings {
    readonly columns: IObservableArray<BoardColumn>;
    readonly currentColumns: readonly BoardColumn[];
    readonly validator: IColumnsValidator;
    readonly dirtyTracker: IColumnsSettingsDirtyTracker;

    /**
     * @returns an observable whose value is a column matching the given id.
     * The observable is a constant object for each column, but its value will
     * be a new object whenever a property is modified.
     */
    getColumn(columnId: string): IObservableValue<BoardColumn> | undefined;
    /**
     * @returns A map of work item type to allowed states for the given column
     */
    getAllowedStateMappings(columnId: string): { [workItemType: string]: string[] };

    /*
     * All mutations to columns must be done through these methods:
     */

    /**
     * @returns the ID of the new column
     */
    addColumn(order?: number): string;
    removeColumn(columnId: string): void;
    setName(columnId: string, name: string): void;
    setOrder(columnId: string, order: number): void;
    setDescription(columnId: string, description: string | null): void;
    setWorkInProgressLimit(columnId: string, wipLimit: number): void;
    setSplitColumn(columnId: string, isSplit: boolean): void;
    setStateMapping(columnId: string, workItemType: string, state: string): void;
}

export function makeColumnsSettings(boardData: BoardData): IColumnsSettings {
    return new ColumnsSettings(boardData);
}

class ColumnsSettings implements IColumnsSettings {
    public validator: IColumnsValidator;
    public dirtyTracker: IInternalColumnsSettingsDirtyTracker;
    private _originalColumns: BoardColumn[];
    private _observableColumnsById: Map<string, IObservableValue<BoardColumn>>;
    private _columns: IObservableArray<BoardColumn>;
    private _allowedMappings: IAllowedMappings;

    constructor(private boardData: BoardData) {
        this._originalColumns = this.boardData.boardModel.boardSettings.columns;
        this._allowedMappings = this.boardData.boardModel.boardSettings.allowedMappings;

        this._observableColumnsById = new Map();
        for (const column of this._originalColumns) {
            this._observableColumnsById.set(column.id, new ObservableValue(column));
        }
        this._columns = new ObservableArray();
        this.refreshColumnArray();

        this.validator = new ColumnsValidator(this._allowedMappings);
        this.dirtyTracker = new ColumnsSettingsDirtyTracker();

        this.validator.validate(this.currentColumns);
    }

    public get columns() {
        return this._columns;
    }

    public get currentColumns() {
        return [...this._observableColumnsById.values()].map(obsCol => obsCol.value).sort((c1, c2) => c1.order - c2.order);
    }

    public getAllowedStateMappings(columnId: string): { [workItemType: string]: string[] } {
        const column = this.currentColumns.find(c => c.id === columnId);
        if (!column) {
            return {};
        }

        return this._allowedMappings["" + column.columnType] ?? {};
    }

    public addColumn(order?: number): string {
        const { id, name } = this.getNewColumnIdAndName();

        const stateMappings: { [workItemTypeName: string]: string } = {};
        for (const [workItemTypeName, allowedStates] of Object.entries(this._allowedMappings["" + BoardColumnType.InProgress])) {
            stateMappings[workItemTypeName] = allowedStates[0] ?? "";
        }

        this._observableColumnsById.set(
            id,
            new ObservableValue({
                id,
                name,
                columnType: BoardColumnType.InProgress,
                description: null as unknown as string, // The type does not allow null, but null is what we expect.
                isDeleted: false,
                isSplit: false,
                itemLimit: 5,
                stateMappings,
                order: -1 // This will be fixed in setOrder
            })
        );

        this.setOrder(id, order ?? 1);

        return id;
    }

    public removeColumn(columnId: string) {
        this._observableColumnsById.delete(columnId);
        this.refreshColumnArray();
        this.dirtyTracker.checkDirty(this._originalColumns, this.currentColumns);
    }

    public getColumn(columnId: string): IObservableValue<BoardColumn> | undefined {
        return this._observableColumnsById.get(columnId);
    }

    public setName(columnId: string, name: string): void {
        this.setColumnValue(columnId, "name", name, true);
    }

    public setOrder(columnId: string, order: number): void {
        const currentOrder = this._observableColumnsById.get(columnId)?.value.order;
        if (currentOrder === undefined || order === currentOrder) {
            return;
        }

        // First, "take this column out" by decrementing the order of any columns with orders greater
        // than this column's current order. -1 indicates that this column hasn't been placed yet.
        if (currentOrder !== -1) {
            for (const col of [...this._observableColumnsById.values()]) {
                if (col.value.order > currentOrder) {
                    this.setColumnValue(col.value.id, "order", col.value.order - 1, false);
                }
            }
        }

        // Next, "make a spot for this column" by incrementing the orders of any columns with orders
        // greater than or equal to the desired order
        for (const col of [...this._observableColumnsById.values()]) {
            if (col.value.order >= order) {
                this.setColumnValue(col.value.id, "order", col.value.order + 1, false);
            }
        }

        // Last, set the desired order on the column and reset the tabs.
        this.setColumnValue(columnId, "order", order, true);
    }

    public setDescription(columnId: string, description: string | null): void {
        // The contract specifies that description is a string, but null is allowed.
        this.setColumnValue(columnId, "description", description as string);
    }

    public setWorkInProgressLimit(columnId: string, itemLimit: number): void {
        this.setColumnValue(columnId, "itemLimit", itemLimit);
    }

    public setSplitColumn(columnId: string, isSplit: boolean): void {
        this.setColumnValue(columnId, "isSplit", isSplit);
    }

    public setStateMapping(columnId: string, workItemType: string, state: string): void {
        const existingMappings = this._observableColumnsById.get(columnId)?.value.stateMappings;
        if (!existingMappings) {
            return;
        }

        // Both the state mapping object and the column itself become new objects.
        const stateMappings = { ...existingMappings, [workItemType]: state };
        this.setColumnValue(columnId, "stateMappings", stateMappings);
    }

    /**
     * Rather than mutating the existing BoardColumn, this sets the observable
     * to a new object with the new value set.
     */
    private setColumnValue<K extends keyof BoardColumn>(columnId: string, key: K, value: BoardColumn[K], affectsTabs?: boolean) {
        const existing = this._observableColumnsById.get(columnId);
        if (!existing) {
            return;
        }
        existing.value = { ...existing.value, [key]: value };
        if (affectsTabs) {
            this.refreshColumnArray();
        }
        this.dirtyTracker.checkDirty(this._originalColumns, this.currentColumns);
        this.validator.validate(this.currentColumns);
    }

    private refreshColumnArray() {
        this._columns.value = this.currentColumns;
    }

    getStateMapDirtyWarning(columnId: string): IObservableValue<string | undefined> {
        throw new Error("Method not implemented.");
    }

    /**
     * Returns a new column name (a string like "New Column" or "New Column 1")
     * and a temporary id (a string like "temp-id" or "temp-id-1"). These will
     * not match the title or id of any existing columns.
     * @returns
     */
    private getNewColumnIdAndName(): { id: string; name: string } {
        let id = "temp-id";
        let idSuffix = 0;
        while (this._observableColumnsById.has(id)) {
            id = `temp-id-${++idSuffix}`;
        }

        let name = Resources.DefaultNewColumnName;
        let nameSuffix = 0;
        while (this.currentColumns.map(col => col.name).includes(name)) {
            name = format(Resources.IndexedNewColumnName, ++nameSuffix);
        }

        return { id, name };
    }
}

export interface IColumnsSettingsDirtyTracker {
    /**
     * An observable whose value is `true` if any of the columns has been modified.
     */
    readonly isDirty: IReadonlyObservableValue<boolean>;
    /**
     * An observable whose value is a string explaining the implications of
     * having modified state mappings if a state mapping has been modified, and undefined otherwise.
     */
    getStateMappingDirtyWarning(columnId: string, workItemType: string): IReadonlyObservableValue<string | undefined>;
}

/**
 * Internal part of the dirty checker interface - external consumers should not need to trigger checking manually.
 */
interface IInternalColumnsSettingsDirtyTracker extends IColumnsSettingsDirtyTracker {
    /**
     * Re-evaluate the dirty status.
     */
    checkDirty(originalColumns: BoardColumn[], columns: BoardColumn[]): void;
}

class ColumnsSettingsDirtyTracker implements IInternalColumnsSettingsDirtyTracker {
    public isDirty: IObservableValue<boolean> = new ObservableValue(false);

    private _stateMappingDirtyMessages: Map<string, IObservableValue<string | undefined>> = new Map();

    public checkDirty(originalColumns: BoardColumn[], columns: BoardColumn[]): void {
        this.setClean();

        for (const originalColumn of originalColumns) {
            const columnId = originalColumn.id;
            const matchingColumn = columns.find(c => c.id === columnId);

            if (matchingColumn === originalColumn) {
                // All of the mutations to the settings create new objects, so if
                // we have object equality then this column is not dirty.
                continue;
            }

            // It is still possible that the settings are not dirty, because a
            // change could have been made and then undone.

            if (
                !matchingColumn ||
                matchingColumn.name !== originalColumn.name ||
                matchingColumn.itemLimit !== originalColumn.itemLimit ||
                matchingColumn.isSplit !== originalColumn.isSplit ||
                matchingColumn.description !== originalColumn.description ||
                matchingColumn.order !== originalColumn.order
            ) {
                this.setDirty();
            }

            // Check if any state mappings have been modified, and update messages accordingly.
            if (matchingColumn) {
                for (const [workItemTypeName, newState] of Object.entries(matchingColumn.stateMappings)) {
                    const originalState = originalColumn.stateMappings[workItemTypeName]!;
                    if (newState !== originalState) {
                        this.setDirty();
                        this.setStateMappingDirtyWarning(originalColumn.id, workItemTypeName, originalState, newState);
                    }
                }
            }
        }
    }

    private setClean() {
        if (this.isDirty.value) {
            this.isDirty.value = false;
        }

        // Clear out any dirtiness-related messages
        for (const obs of this._stateMappingDirtyMessages.values()) {
            obs.value = undefined;
        }
    }

    private setDirty() {
        if (!this.isDirty.value) {
            this.isDirty.value = true;
        }
    }

    private setStateMappingDirtyWarning(columnId: string, workItemTypeName: string, originalState: string, newState: string) {
        const key = this.makeStateMappingKey(columnId, workItemTypeName);
        const message = format(Resources.StateMappingChangedWarning, workItemTypeName, originalState, newState);
        if (this._stateMappingDirtyMessages.has(key)) {
            this._stateMappingDirtyMessages.get(key)!.value = message;
        } else {
            this._stateMappingDirtyMessages.set(key, new ObservableValue(message));
        }
    }

    public getStateMappingDirtyWarning(columnId: string, workItemType: string): IReadonlyObservableValue<string | undefined> {
        const key = this.makeStateMappingKey(columnId, workItemType);
        if (!this._stateMappingDirtyMessages.has(key)) {
            this._stateMappingDirtyMessages.set(key, new ObservableValue(undefined));
        }
        return this._stateMappingDirtyMessages.get(key)!;
    }

    private makeStateMappingKey(columnId: string, workItemType: string) {
        return `columnId:${columnId};workItemType:${workItemType}`;
    }
}
